// recipeProcessingService.js
import { fetchRecipeDetails, getRecipeImageWithRecovery, extractJsonStringField, extractJsonObjectField } from '../../services/apiService';
import { getCurrentLanguage } from '../../services/i18n';
import i18n from '../../services/i18n';
import { createRecipeDetailsPrompt } from '../../utils/recipeDetailsPromptBuilder';
import { clampCookingTime } from '../../utils/cookingTimeLimits';
import { computeRecipeFreshnessDays, normalizeRecipeFreshness } from '../../utils/recipeFreshnessRules';
import { createCookingInstructionsRetryPrompt, validateCookingInstructions } from '../../utils/recipeInstructionRules';

// Track which recipes are already being processed to prevent duplicate calls
let processingRecipes = {};
let processedRecipes = {};

// Enhanced function to fetch recipe details and then generate image
// In the new flow, images are generated from o3-mini description, and details use matched ingredients
export const fetchRecipeDetailsAndGenerateImage = async (recipe, setMealPlan, setImagesLoading, addReasoningStep, userCountry = null, matchedProducts = null, minStorageDays = 3) => {
  // Skip if recipe is invalid or already being processed
  if (!recipe || processingRecipes[recipe.title]) {
    return;
  }
  
  // For replacements (fetchingDetailsForImage=true), always re-process even if cached
  // This ensures replaced recipes get fresh details + image
  if (processedRecipes[recipe.title] && !recipe.fetchingDetailsForImage) {
    return;
  }
  
  // Mark this recipe as being processed to prevent duplicate calls
  processingRecipes[recipe.title] = true;
  
  // Mark that we're fetching details for this recipe
  setMealPlan(prev => 
    prev.map(r => r.title === recipe.title ? {...r, fetchingDetailsForImage: true} : r)
  );
  
  const _t0 = Date.now();
  const _elapsed = () => ((Date.now() - _t0) / 1000).toFixed(2) + 's';
  const _isReplace = !!recipe.fetchingDetailsForImage;
  console.log(`[RecipeProcessor] ▶ START "${recipe.title}" (${_isReplace ? 'replacement' : 'initial'}, minStorage=${minStorageDays}, matchedProducts=${matchedProducts?.length || 0} cats)`);
  
  try {
    // Build the recipe details prompt via the shared builder. The builder owns ingredient
    // context (hint-first matching with substitutions), language, complexity, servings,
    // shelf-life catalog, cooking-instruction schema, and self-check rules — so this
    // replacement path stays equivalent to the initial-generation path.
    const optimizedPromptData = createRecipeDetailsPrompt(recipe, matchedProducts, { minStorageDays, userCountry });
    const prompt = optimizedPromptData.prompt;
    const ingredientsWithContext = optimizedPromptData.ingredientsWithContext;
    const substitutions = optimizedPromptData.substitutions || [];
    const ingredientNames = optimizedPromptData.ingredientNames || '';

    console.log(`[RecipeProcessor] 📋 [${_elapsed()}] Prompt built for "${recipe.title}" (${ingredientNames.split(', ').filter(Boolean).length} ingredients, ${substitutions.length} substitutions)`);

    // Image will be generated from visual_description after recipe details arrive
    setImagesLoading(prev => ({ ...prev, [recipe.title]: true }));

    // === DEBUG: Store compact recipe details context ===
    if (window.__REPLACE_DEBUG && recipe.fetchingDetailsForImage) {
      window.__REPLACE_DEBUG.recipeDetails = {
        ingredientsWithContext,
        substitutions,
        matchedProductsReceived: !!matchedProducts,
        matchedProductsCount: matchedProducts?.length || 0,
      };
    }
    // === END DEBUG ===

    // Set per-recipe loading status
    setMealPlan(prev => prev.map(r => r.title === recipe.title ? { ...r, loadingStatus: 'details' } : r));

    // Early image generation: extract visual_description from streaming buffer
    // and fire getRecipeImage as soon as the field is complete (before full response)
    let earlyImageFired = false;
    let earlyImagePromise = null;
    let _earlyImageTitle = null;
    let _chunkCount = 0;
    let _titleExtractedAt = null;

    const onChunkForEarlyImage = (buffer) => {
      _chunkCount++;
      if (earlyImageFired) return;

      // Track updated_title extraction moment (it should arrive BEFORE visual_description)
      if (!_titleExtractedAt) {
        const earlyTitleCheck = extractJsonStringField(buffer, 'updated_title');
        if (earlyTitleCheck.complete) {
          _titleExtractedAt = Date.now();
          console.log(`[RecipeProcessor] 📝 [${_elapsed()}] updated_title extracted at chunk #${_chunkCount}: "${earlyTitleCheck.value?.trim()}" (original: "${recipe.title}")`);
        }
      }

      const visResult = extractJsonStringField(buffer, 'visual_description');
      if (!visResult.complete) {
        // Fallback: AI sometimes generates visual_description as a JSON object instead of string
        const visObjResult = extractJsonObjectField(buffer, 'visual_description');
        if (!visObjResult.complete) return;

        earlyImageFired = true;
        const visualDescObj = visObjResult.value;

        const titleResult = extractJsonStringField(buffer, 'updated_title');
        const updatedTitle = (titleResult.complete && titleResult.value?.trim())
          ? titleResult.value.trim()
          : recipe.title;

        const titleOrder = _titleExtractedAt ? `title first \u2713 (${((Date.now() - _titleExtractedAt) / 1000).toFixed(2)}s before visual_desc)` : 'title NOT extracted before visual_desc \u26a0\ufe0f';
        console.log(`[RecipeProcessor] \ud83d\ude80 [${_elapsed()}] visual_description extracted as OBJECT at chunk #${_chunkCount} \u2192 image gen START (${titleOrder})`);
        console.log(`[RecipeProcessor]    title="${updatedTitle}", visual_desc keys: ${Object.keys(visualDescObj).join(', ')}`);

        setImagesLoading(prev => ({ ...prev, [updatedTitle]: true }));
        setMealPlan(prev => prev.map(r => r.title === recipe.title ? { ...r, loadingStatus: 'image' } : r));
        _earlyImageTitle = updatedTitle;

        earlyImagePromise = getRecipeImageWithRecovery(updatedTitle, ingredientNames, visualDescObj)
          .then((imageURL) => {
            if (imageURL) {
              setMealPlan(prev => prev.map(r =>
                (r.title === recipe.title || r.title === updatedTitle)
                  ? { ...r, imageURL, loadingStatus: null } : r
              ));
            }
            setImagesLoading(prev => {
              const next = { ...prev, [updatedTitle]: false };
              if (updatedTitle !== recipe.title) delete next[recipe.title];
              return next;
            });
            return imageURL;
          })
          .catch((err) => {
            console.warn(`[RecipeProcessor] \u2717 Early image FAILED for "${updatedTitle}":`, err.message);
            setMealPlan(prev => prev.map(r =>
              (r.title === recipe.title || r.title === updatedTitle)
                ? { ...r, loadingStatus: null } : r
            ));
            setImagesLoading(prev => ({ ...prev, [updatedTitle]: false }));
            return null;
          });
        return;
      }

      earlyImageFired = true;
      const visualDesc = visResult.value;

      // Extract updated_title from the stream (comes before visual_description in JSON schema)
      const titleResult = extractJsonStringField(buffer, 'updated_title');
      const updatedTitle = (titleResult.complete && titleResult.value?.trim()) 
        ? titleResult.value.trim() 
        : recipe.title;

      const titleOrder = _titleExtractedAt ? `title first ✓ (${((Date.now() - _titleExtractedAt) / 1000).toFixed(2)}s before visual_desc)` : 'title NOT extracted before visual_desc ⚠️';
      console.log(`[RecipeProcessor] 🚀 [${_elapsed()}] visual_description extracted at chunk #${_chunkCount} → image gen START (${titleOrder})`)
      console.log(`[RecipeProcessor]    title="${updatedTitle}", visual_desc=${visualDesc.length} chars`);

      setImagesLoading(prev => ({ ...prev, [updatedTitle]: true }));
      setMealPlan(prev => prev.map(r => r.title === recipe.title ? { ...r, loadingStatus: 'image' } : r));
      _earlyImageTitle = updatedTitle;

      earlyImagePromise = getRecipeImageWithRecovery(updatedTitle, ingredientNames, visualDesc)
        .then((imageURL) => {
          if (imageURL) {
            console.log(`[RecipeProcessor] 🖼️ [${_elapsed()}] Early image READY for "${updatedTitle}" (${imageURL.substring(0, 60)}...)`);
            // Update by both original and updated title in case title changed
            setMealPlan(prev => prev.map(r => 
              (r.title === recipe.title || r.title === updatedTitle) 
                ? { ...r, imageURL, loadingStatus: null } 
                : r
            ));
          }
          setImagesLoading(prev => {
            const next = { ...prev, [updatedTitle]: false };
            if (updatedTitle !== recipe.title) delete next[recipe.title];
            return next;
          });
          return imageURL;
        })
        .catch((err) => {
          console.warn(`[RecipeProcessor] ✗ [${_elapsed()}] Early image FAILED for "${updatedTitle}":`, err.message || err);
          setImagesLoading(prev => {
            const next = { ...prev, [updatedTitle]: false };
            if (updatedTitle !== recipe.title) delete next[recipe.title];
            return next;
          });
          return null;
        });
    };

    // Fetch recipe details — onChunk monitors for early visual_description
    let instructions = await fetchRecipeDetails(
      prompt,
      onChunkForEarlyImage,
      (reasoningText) => reasoningText && addReasoningStep(`Recipe Generation: ${reasoningText}`),
      {
        title: recipe.title,
        description: recipe.description,
        autoGenerateImage: false,
      }
    );

    let instructionValidation = validateCookingInstructions(instructions);
    if (!instructionValidation.isValid) {
      console.warn(`[RecipeProcessor] ⚠️ [${_elapsed()}] Invalid cooking instructions for "${recipe.title}": ${instructionValidation.issues.join('; ')}. Retrying once.`);
      instructions = await fetchRecipeDetails(
        createCookingInstructionsRetryPrompt(prompt, instructionValidation.issues),
        onChunkForEarlyImage,
        (reasoningText) => reasoningText && addReasoningStep(`Recipe Generation Retry: ${reasoningText}`),
        {
          title: recipe.title,
          description: recipe.description,
          autoGenerateImage: false,
        }
      );

      instructionValidation = validateCookingInstructions(instructions);
      if (!instructionValidation.isValid) {
        console.warn(`[RecipeProcessor] ⚠️ [${_elapsed()}] Cooking instructions still imperfect for "${recipe.title}": ${instructionValidation.issues.join('; ')}`);
        if (instructions && typeof instructions === 'object') {
          instructions._instructionQualityIssues = instructionValidation.issues;
        }
      } else {
        console.log(`[RecipeProcessor] ✓ [${_elapsed()}] Cooking instructions repaired for "${recipe.title}"`);
      }
    }
    
    console.log(`[RecipeProcessor] 📥 [${_elapsed()}] Stream complete for "${recipe.title}" (${_chunkCount} chunks, earlyImageFired=${earlyImageFired})`);

    // Validate instructions - ensure it's an object (not a raw string from failed parsing)
    let validInstructions = (instructions && typeof instructions === 'object')
      ? instructions 
      : { 
          full_description: 'Recipe details could not be loaded. Please try again.',
          cooking_instructions: [],
          tips: [],
          equipment: [],
          _partial: true
        };
    
    // Log if we got partial data
    if (validInstructions._partial) {
      console.warn(`[RecipeProcessor] ⚠️ [${_elapsed()}] PARTIAL/fallback data for "${recipe.title}"`);
    } else {
      console.log(`[RecipeProcessor] ✓ [${_elapsed()}] Full response parsed: title="${validInstructions.updated_title || '(unchanged)'}", cooking_time=${validInstructions.cooking_time || '?'}min, storage=${validInstructions.max_storage_time || '?'}d, steps=${validInstructions.cooking_instructions?.length || 0}, ingredients_storage=${validInstructions.ingredient_storage?.length || 0}`);
    }

    // === DEBUG: Store compact recipe details result ===
    if (window.__REPLACE_DEBUG && recipe.fetchingDetailsForImage) {
      window.__REPLACE_DEBUG.recipeDetailsResult = {
        updatedTitle: validInstructions?.updated_title || null,
        cookingTime: validInstructions?.cooking_time || null,
        maxStorageTime: validInstructions?.max_storage_time || null,
        ingredientStorage: validInstructions?.ingredient_storage || [],
        hasInstructions: !!(validInstructions?.cooking_instructions?.length),
        isPartial: !!validInstructions?._partial,
      };
    }
    // === END DEBUG ===

    normalizeRecipeFreshness(validInstructions, {
      minStorageDays,
      language: getCurrentLanguage(),
      logger: (message) => console.log(`[RecipeProcessor] 📊 ${message}`),
    });
    
    // Check if AI returned an updated title (ingredient mismatch correction)
    // NOTE: named postStreamTitle to avoid shadowing the inner `updatedTitle` from onChunkForEarlyImage
    const postStreamTitle = validInstructions?.updated_title && validInstructions.updated_title.trim()
      ? validInstructions.updated_title.trim()
      : null;
    const titleChanged = postStreamTitle && postStreamTitle !== recipe.title;
    const fullDesc = validInstructions?.full_description;
    const visualDesc = validInstructions?.visual_description;
    
    if (titleChanged) {
      console.log(`[RecipeProcessor] 📝 [${_elapsed()}] Title changed post-stream: "${recipe.title}" → "${postStreamTitle}"`);
    }
    
    // Update the recipe with full instructions when streaming is complete
    setMealPlan(prev => 
      prev.map(r => r.title === recipe.title ? {
        ...r,   
        instructions: validInstructions,
        instructionsFormatted: true,
        fetchingDetailsForImage: false,
        // BUG #3 fix: if early image already arrived (earlyImageFired), don't overwrite
        // loadingStatus back to 'image' — check r.imageURL instead
        loadingStatus: earlyImageFired
          ? (r.imageURL ? null : 'image')
          : (!!(visualDesc || (titleChanged && fullDesc)) ? 'image' : null),
        // Update title if AI corrected it based on actual ingredients
        ...(titleChanged ? {
          title: postStreamTitle,
          originalTitle: r.originalTitle || recipe.title,
        } : {}),
        // Update description from recipe details (reflects actual ingredients)
        ...(fullDesc ? { description: fullDesc } : {}),
        // Update cookingTime from AI-generated details, clamped to the meal's
        // cap so a details overshoot can't replace the validated value.
        cookingTime: clampCookingTime(validInstructions?.cooking_time, r.cookingTime, recipe.mealType, recipe.complexity),
        // Compute maxStorageTime from ingredient_storage data if available
        maxStorageTime: (() => {
          const days = computeRecipeFreshnessDays(validInstructions);
          return days ? `${days}` : (validInstructions?.max_storage_time || r.maxStorageTime);
        })(),
      } : r)
    );
    
    // Image generation: either wait for early image result, or generate post-stream
    const finalTitle = postStreamTitle || recipe.title;
    const imageIngredients = ingredientNames;

    if (earlyImageFired && earlyImagePromise) {
      // Early image was fired during streaming — await its result
      const earlyImageUrl = await earlyImagePromise;
      if (!earlyImageUrl) {
        // Early image failed (all providers returned null) — retry once more
        console.log(`[RecipeProcessor] 🔄 [${_elapsed()}] Early image returned null for "${_earlyImageTitle || finalTitle}" — retrying...`);
        const retryTitle = _earlyImageTitle || finalTitle;
        setImagesLoading(prev => ({ ...prev, [retryTitle]: true }));
        setMealPlan(prev => prev.map(r =>
          (r.title === recipe.title || r.title === retryTitle || r.title === finalTitle)
            ? { ...r, loadingStatus: 'image' } : r
        ));
        try {
          const retryUrl = await getRecipeImageWithRecovery(retryTitle, imageIngredients, visualDesc);
          if (retryUrl) {
            console.log(`[RecipeProcessor] 🖼️ [${_elapsed()}] Retry image READY for "${retryTitle}" (${retryUrl.substring(0, 60)}...)`);
            setMealPlan(prev => prev.map(r =>
              (r.title === recipe.title || r.title === retryTitle || r.title === finalTitle)
                ? { ...r, imageURL: retryUrl, loadingStatus: null } : r
            ));
          } else {
            console.warn(`[RecipeProcessor] ⚠️ [${_elapsed()}] Retry image also returned null for "${retryTitle}"`);
            setMealPlan(prev => prev.map(r =>
              (r.title === recipe.title || r.title === retryTitle || r.title === finalTitle)
                ? { ...r, loadingStatus: null } : r
            ));
          }
          setImagesLoading(prev => ({ ...prev, [retryTitle]: false }));
        } catch (err) {
          console.warn(`[RecipeProcessor] ✗ [${_elapsed()}] Retry image FAILED for "${retryTitle}":`, err.message || err);
          setImagesLoading(prev => ({ ...prev, [retryTitle]: false }));
          setMealPlan(prev => prev.map(r =>
            (r.title === recipe.title || r.title === retryTitle || r.title === finalTitle)
              ? { ...r, loadingStatus: null } : r
          ));
        }
      }
    } else if (!earlyImageFired && !!(visualDesc || (titleChanged && fullDesc))) {
      // No early image was fired — generate post-stream
      console.log(`[RecipeProcessor] 🖼️ [${_elapsed()}] Post-stream image gen START for "${finalTitle}" (earlyImageFired=${earlyImageFired})`);
      
      // Clean up old title's imagesLoading if title changed
      if (titleChanged) {
        setImagesLoading(prev => {
          const next = { ...prev };
          delete next[recipe.title];
          next[finalTitle] = true;
          return next;
        });
      } else {
        setImagesLoading(prev => ({ ...prev, [finalTitle]: true }));
      }
      
      try {
        const imageURL = await getRecipeImageWithRecovery(finalTitle, imageIngredients, visualDesc);
        if (imageURL) {
          console.log(`[RecipeProcessor] 🖼️ [${_elapsed()}] Post-stream image READY for "${finalTitle}" (${imageURL.substring(0, 60)}...)`);
          setMealPlan(prev => prev.map(r => r.title === finalTitle ? { ...r, imageURL, loadingStatus: null } : r));
        } else {
          console.warn(`[RecipeProcessor] ⚠️ [${_elapsed()}] Post-stream image returned null for "${finalTitle}"`);
          setMealPlan(prev => prev.map(r => r.title === finalTitle ? { ...r, loadingStatus: null } : r));
        }
        setImagesLoading(prev => ({ ...prev, [finalTitle]: false }));
      } catch (err) {
        console.warn(`[RecipeProcessor] ✗ [${_elapsed()}] Post-stream image FAILED for "${finalTitle}":`, err.message || err);
        setImagesLoading(prev => ({ ...prev, [finalTitle]: false }));
        setMealPlan(prev => prev.map(r => r.title === finalTitle ? { ...r, loadingStatus: null } : r));
      }
    }

    console.log(`[RecipeProcessor] ■ [${_elapsed()}] DONE "${postStreamTitle || recipe.title}" — earlyImage=${earlyImageFired}, titleChanged=${!!titleChanged}, loadingStatus=${earlyImageFired ? 'checked r.imageURL' : (visualDesc ? 'image' : 'null')}`);

    // Mark as processed (track both old and new title if changed)
    processedRecipes[recipe.title] = true;
    if (titleChanged) {
      processedRecipes[postStreamTitle] = true;
    }
    
    // Return instructions so callers (e.g. handleSelectAlternative) can use ingredient_storage
    return validInstructions;
    
  } catch (error) {
    console.error(`[RecipeProcessor] ✗ [${_elapsed()}] ERROR for "${recipe.title}":`, error.message || error);
    setMealPlan(prev => 
      prev.map(r => r.title === recipe.title ? {...r, fetchingDetailsForImage: false, loadingStatus: null} : r)
    );
    setImagesLoading(prev => ({...prev, [recipe.title]: false}));
  } finally {
    delete processingRecipes[recipe.title];
  }
};

// Get instructions for a recipe
export const getRecipeInstructions = async (recipe, setMealPlan, setSelectedRecipe, addReasoningStep, userCountry = null) => {
  // If recipe already has instructions, no need to fetch again
  if (recipe.instructions) return;

  // Check if we're already processing this recipe
  if (processingRecipes[recipe.title]) {
    console.log(`Recipe ${recipe.title} is already being processed, waiting for completion`);
    return;
  }

  try {
    setMealPlan((prev) =>
      prev.map((r) =>
        r.title === recipe.title ? { ...r, loadingInstructions: true } : r
      )
    );

    // Determine language based on country
    const isEstonia = userCountry === 'Estonia';
    const languageInstruction = isEstonia 
      ? "Provide instructions in Russian language." 
      : "Provide instructions in English.";

    // Handle ingredients format - convert object to array format if needed
    let ingredientsText = '';
    if (Array.isArray(recipe.ingredients)) {
      ingredientsText = recipe.ingredients.map((i) => `${i.qty} ${i.product}`).join(', ');
    } else if (recipe.ingredients && typeof recipe.ingredients === 'object') {
      ingredientsText = Object.entries(recipe.ingredients).map(([product, qty]) => `${qty} ${product}`).join(', ');
    } else {
      ingredientsText = 'No ingredients specified';
    }

    const prompt = `Provide detailed cooking instructions for ${recipe.title}. Ingredients: ${ingredientsText}. ${languageInstruction}`;

    // Fetch instructions
    const instructions = await fetchRecipeDetails(
      prompt,
      (partialInstructions) => {
        setMealPlan((prev) =>
          prev.map((r) =>
            r.title === recipe.title
              ? { ...r, instructions: partialInstructions }
              : r
          )
        );
        if (setSelectedRecipe) {
          setSelectedRecipe((prev) => prev && prev.title === recipe.title ? 
            { ...prev, instructions: partialInstructions } : prev);
        }
      },
      (reasoningText) => reasoningText && addReasoningStep(reasoningText)
    );

    setMealPlan((prev) =>
      prev.map((r) =>
        r.title === recipe.title
          ? { ...r, instructions, loadingInstructions: false }
          : r
      )
    );
  } catch (error) {
    console.error('Error fetching recipe details:', error);
    setMealPlan((prev) =>
      prev.map((r) =>
        r.title === recipe.title
          ? { ...r, loadingInstructions: false }
          : r
      )
    );
  }
  
};

// Component for displaying recipe instructions
export const RecipeInstructions = ({ instructions }) => {
  // If instructions is a string, try to parse it as JSON first
  if (typeof instructions === 'string') {
    try {
      instructions = JSON.parse(instructions);
    } catch (e) {
      // If parsing failed, just render the string directly
      return <div className="text-[var(--Color-Grays-Gray-7,#65635D)] text-[14px] leading-[24px] whitespace-pre-wrap">{instructions}</div>;
    }
  }
  
  // If instructions is an object with the expected structure
  if (instructions && typeof instructions === 'object') {
    // Extract cooking instructions array - handle both field names
    let cookingSteps = instructions.cooking_instructions || [];
    
    return (
      <div className="space-y-4">
        {/* Cooking Instructions */}
        {cookingSteps && cookingSteps.length > 0 ? (
          <ol className="space-y-4">
            {cookingSteps.map((step, index) => {
              // Handle different step formats
              
              // CASE 1: Step is a string that looks like a JSON object
              if (typeof step === 'string' && step.trim().startsWith('{')) {
                try {
                  // Try to parse it as JSON
                  const parsedStep = JSON.parse(step);
                  
                  return (
                    <li key={index} className="flex items-start">
                      <span className="text-[var(--Color-Grays-Gray-10,#0D0C07)] text-[14px] font-semibold mr-3 mt-1 min-w-[24px]">{index + 1}.</span>
                      <div className="flex-1">
                        {parsedStep.step_title || parsedStep.title ? (
                          <p className="text-[var(--Color-Grays-Gray-10,#0D0C07)] text-[14px] font-semibold leading-[24px] mb-2">{parsedStep.step_title || parsedStep.title}</p>
                        ) : null}
                        
                        {parsedStep.details && Array.isArray(parsedStep.details) && (
                          <ul className="space-y-1">
                            {parsedStep.details.map((detail, detailIdx) => (
                              <li key={detailIdx} className="text-[var(--Color-Grays-Gray-7,#65635D)] text-[14px] leading-[24px] flex items-start">
                                <span className="mr-2 text-[var(--Color-Grays-Gray-10,#0D0C07)]">•</span>
                                <span>
                                  {typeof detail === 'string' ? detail : 
                                   detail.detail ? detail.detail : 
                                   JSON.stringify(detail)}
                                </span>
                              </li>
                            ))}
                          </ul>
                        )}
                      </div>
                    </li>
                  );
                } catch (e) {
                  // If parsing fails, render as plain text
                  return (
                    <li key={index} className="flex items-start">
                      <span className="text-[var(--Color-Grays-Gray-10,#0D0C07)] text-[14px] font-semibold mr-3 mt-1 min-w-[24px]">{index + 1}.</span>
                      <div className="flex-1">
                        <p className="text-[var(--Color-Grays-Gray-7,#65635D)] text-[14px] leading-[24px]">{step}</p>
                      </div>
                    </li>
                  );
                }
              }
              
              // CASE 2: Step is already an object with step_title/title and details
              if (step && typeof step === 'object') {
                return (
                  <li key={index} className="flex items-start">
                    <span className="text-[var(--Color-Grays-Gray-10,#0D0C07)] text-[14px] font-semibold mr-3 mt-1 min-w-[24px]">{index + 1}.</span>
                    <div className="flex-1">
                      {step.step_title || step.title ? (
                        <p className="text-[var(--Color-Grays-Gray-10,#0D0C07)] text-[14px] font-semibold leading-[24px] mb-2">{step.step_title || step.title}</p>
                      ) : null}
                      
                      {step.details && Array.isArray(step.details) && (
                        <ul className="space-y-1">
                          {step.details.map((detail, detailIdx) => (
                            <li key={detailIdx} className="text-[var(--Color-Grays-Gray-7,#65635D)] text-[14px] leading-[24px] flex items-start">
                              <span className="mr-2 text-[var(--Color-Grays-Gray-10,#0D0C07)]">•</span>
                              <span>
                                {typeof detail === 'string' ? detail : 
                                 detail.detail ? detail.detail : 
                                 JSON.stringify(detail)}
                              </span>
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  </li>
                );
              }
              
              // CASE 3: Step is a plain string (not JSON)
              if (typeof step === 'string') {
                return (
                  <li key={index} className="flex items-start">
                    <span className="text-[var(--Color-Grays-Gray-10,#0D0C07)] text-[14px] font-semibold mr-3 mt-1 min-w-[24px]">{index + 1}.</span>
                    <div className="flex-1">
                      <p className="text-[var(--Color-Grays-Gray-7,#65635D)] text-[14px] leading-[24px]">{step}</p>
                    </div>
                  </li>
                );
              }
              
              // Default case if none of the above match
              return (
                <li key={index} className="flex items-start">
                  <span className="text-[var(--Color-Grays-Gray-10,#0D0C07)] text-[14px] font-semibold mr-3 mt-1 min-w-[24px]">{index + 1}.</span>
                  <div className="flex-1">
                    <p className="text-[var(--Color-Grays-Gray-7,#65635D)] text-[14px] leading-[24px]">{typeof step === 'string' ? step : JSON.stringify(step)}</p>
                  </div>
                </li>
              );
            })}
          </ol>
        ) : (
          <p className="text-[var(--Color-Grays-Gray-7,#65635D)] text-[14px] leading-[24px]">{i18n.t('common:recipe.noInstructions', 'Cooking steps are not available yet.')}</p>
        )}
      </div>
    );
  }
  
  // Fallback for any other case
  return <p className="text-[var(--Color-Grays-Gray-7,#65635D)] text-[14px] leading-[24px]">{i18n.t('common:recipe.instructionsNotAvailable', 'Cooking steps are not available.')}</p>;
};

// Reset the recipe processing state (useful when starting a new session)
export const resetRecipeProcessingState = () => {
  processingRecipes = {};
  processedRecipes = {};
};